"use client";
import type {
  AuditFormProps,
  SessionProgress,
  SetSessionProgress,
} from "../../../types/audit-form";

import type { AuditFileMetadata } from "../../../types/audit-file";
import { useLatest } from "../../../hooks/use-latest";
import { useImageDescriptions } from "../../../hooks/use-image-descriptions";
import {
  errorMessage,
  requireMutationSuccess,
} from "../../../utils/mutationResult.js";

import {
  AlertCircle,
  Camera,
  Eye,
  Loader2,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { LoadingIndicator } from "../../../components/ui/loading-indicator";

import { useDebouncedSave } from "../../../hooks/use-debounced-save";
import { useDraftAutosave } from "../../../hooks/use-draft-autosave";
import { useStableMemoComponent } from "../../../hooks/use-stable-memo-component";
import {
  useVersionAwareFiles,
  useVersionAwareSiteData,
} from "../../../hooks/use-version-aware-data";
import { useNotification } from "../../../hooks/useNotification";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  deleteFile,
  fetchFiles,
  updateFile,
  uploadMultipleFiles,
} from "../../../redux/slices/fileSlice.js";
import { updateSiteData } from "../../../redux/slices/siteDataSlice.js";
import { operationalPlanningAspectsProgress } from "../../../utils/componentUtils.js";
import { notificationHelpers } from "../../../utils/notificationHelpers";
import { OfflineImage } from "../../shared/offline-image";
import {
  OptimizedCheckboxGroup,
  OptimizedInput,
  OptimizedTextarea,
} from "../optimized-form-field";
import { ImageCapturePicker } from "../shared/image-capture-picker";
import { LocalSectionImportControl } from "../shared/local-section-import-control";

interface PlanningAspectsFormProps extends AuditFormProps {
  siteId: string;
  templateId: string;
  reportId: string;
  readOnly?: boolean;
  isViewingHistoricalVersion?: boolean;
  versionData?: any;

  sessionProgress: SessionProgress;
  setSessionProgress: SetSessionProgress;
  previousCompletedReports?: any[];
}

interface PlanningAspectsImage extends AuditFileMetadata {
  id: string;
  name: string;
  url: string;
  type: string;
  size: number;
  description: string;
  uploadDate: Date;
}

interface PlanningAspectsData {
  // Screening conditions
  screeningConditions: string;

  // Landscape management plan
  landscapeManagementPlan: string;

  // Site designations (multi-select)
  siteDesignations: string[];
  otherDesignation: string;

  // Images
  planningAspectsImages: PlanningAspectsImage[];
}

const initialData: PlanningAspectsData = {
  screeningConditions: "",
  landscapeManagementPlan: "",
  siteDesignations: [],
  otherDesignation: "",
  planningAspectsImages: [],
};

const siteDesignationOptions = [
  {
    value: "protected-landscape",
    label: "Protected landscape (e.g. National Park)",
  },
  {
    value: "ecologically-sensitive-zone",
    label: "Ecologically sensitive zone",
  },
  { value: "flood-management-zone", label: "Flood management zone" },
  {
    value: "high-value-agricultural-land",
    label: "High-value agricultural land",
  },
  { value: "contaminated-land", label: "Contaminated land" },
  {
    value: "unexploded-ordnance",
    label: "Previous site of unexploded ordnance",
  },
  { value: "none", label: "None" },
  { value: "other", label: "Other" },
];

export const OperationalSitePlanningAspectsForm = memo(
  function PlanningAspectsForm({
    siteId,
    templateId,
    reportId,
    sessionProgress,
    setSessionProgress,
    readOnly = false,
    previousCompletedReports = [],
  }: PlanningAspectsFormProps) {
    const { notify } = useNotification();

    const { loading } = useAppSelector((state) => state?.files);
    const files = useVersionAwareFiles(readOnly);
    const [imageDescriptions, setImageDescriptions] =
      useImageDescriptions(files);
    const [formData, setFormData] = useState<PlanningAspectsData>(initialData);
    const formDataRef = useLatest(formData);
    const [selectedImage, setSelectedImage] =
      useState<PlanningAspectsImage | null>(null);
    const [imageToDelete, setImageToDelete] = useState<string | null>(null);
    const [uploadingImages, setUploadingImages] = useState<{
      [key: string]: boolean;
    }>({});

    const dispatch = useAppDispatch();

    // // Load saved data from localStorage
    // useEffect(() => {
    //   const savedData = localStorage.getItem(`planning-aspects-data-${siteId}`);
    //   if (savedData) {
    //     try {
    //       const parsedData = JSON.parse(savedData);
    //       resetFormBaseline(parsedData);
    //       setFormData(parsedData);
    //     } catch (error) {
    //       console.error("Failed to load saved data:", error);
    //     }
    //   }
    // }, [siteId]);

    // Fetch existing files
    useEffect(() => {
      dispatch(
        fetchFiles({
          siteId,
          reportId,
        }),
      );
    }, [dispatch, siteId, reportId]);

    // Load version data when viewing historical version
    const currentSiteData = useVersionAwareSiteData(readOnly);

    // Autosave unsaved edits to IndexedDB so nothing is lost if the app closes
    // before the debounced server save fires (e.g. offline, tab closed, phone
    // loses power).
    const draftKey =
      !readOnly && reportId
        ? `report:${reportId}:operational-planning-aspects`
        : null;
    const { restoreDraft, clearSavedDraft } = useDraftAutosave(
      draftKey,
      formData,
    );
    const draftRestoredRef = useRef(false);

    // Restore any unsaved draft once on mount, before server data can
    // overwrite it below.
    useEffect(() => {
      if (!draftKey || draftRestoredRef.current) return;
      draftRestoredRef.current = true;
      restoreDraft().then((draft) => {
        if (draft?.data) {
          setFormData((prev) => ({ ...prev, ...draft.data }));
        }
      });
    }, [draftKey, restoreDraft]);

    // Once local form state matches the confirmed server record, the
    // IndexedDB draft is redundant — clear it so a future mount doesn't
    // restore stale local data over a fresher server record.
    useEffect(() => {
      if (!draftKey || !currentSiteData) return;
      if (JSON.stringify(currentSiteData) === JSON.stringify(formData)) {
        clearSavedDraft();
      }
    }, [draftKey, currentSiteData, formData, clearSavedDraft]);

    useEffect(() => {
      // Update session progress
      const prog = operationalPlanningAspectsProgress(formData);
      setSessionProgress((prev) => ({
        ...prev,
        [templateId]: { ...prev[templateId], "planning-aspects": prog },
      }));
    }, [formData, setSessionProgress, templateId]);

    // Save handler
    const handleSave = useCallback(
      async (data: PlanningAspectsData) => {
        try {
          const mutationResult1 = await requireMutationSuccess(
            dispatch(
              updateSiteData({
                siteDataId: siteId,
                siteDataInfo: { planningAspectsData: data },
              }),
            ),
          );
          const config = notificationHelpers.formSaveSuccess("Site Data", [
            mutationResult1,
          ]);
          notify(config.message, config.type, config.options);
        } catch (error) {
          const config = notificationHelpers.formSaveError(
            "Site Data",
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);

          throw error;
        }
      },
      [siteId, dispatch, notify],
    );

    // Debounced save
    const {
      isSaving,
      lastSaved,
      hasUnsavedChanges,
      saveError,
      forceSave,
      resetBaseline: resetFormBaseline,
    } = useDebouncedSave(formData, {
      delay: 7500,
      onSave: handleSave,
      onError: (error) => console.error("Save error:", error),
      readOnly,
    });

    useEffect(() => {
      if (currentSiteData) {
        const newFormData = {
          ...formDataRef.current,
          ...(currentSiteData?.planningAspectsData || {}),
        };
        resetFormBaseline(newFormData);
        setFormData(newFormData);
      }
    }, [currentSiteData, resetFormBaseline, formDataRef]);

    // Save handler for image descriptions
    const handleSaveImages = useCallback(
      async (descriptions: Record<string, string>) => {
        try {
          const mutationResult2 = await Promise.all(
            Object.entries(descriptions).map(([fileId, fileDescription]) =>
              requireMutationSuccess(
                dispatch(updateFile({ fileId, fileData: { fileDescription } })),
              ),
            ),
          );
          const config = notificationHelpers.formSaveSuccess(
            "Image Descriptions",
            [mutationResult2],
          );
          notify(config.message, config.type, config.options);
        } catch (error) {
          const config = notificationHelpers.formSaveError(
            "Image Descriptions",
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);

          throw error;
        }
      },
      [dispatch, notify],
    );

    // Debounced save for image descriptions (5000ms delay)
    const {
      isSaving: isImageSaving,
      lastSaved: lastSavedImage,
      hasUnsavedChanges: hasImageUnsavedChanges,
      saveError: imageSaveError,
    } = useDebouncedSave(imageDescriptions, {
      delay: 5000,
      onSave: handleSaveImages,
      onError: (error) => console.error("Image description save error:", error),
      readOnly,
    });

    // Combine form and image save states
    const isSavingCombined = isSaving || isImageSaving;
    const hasUnsavedChangesCombined =
      hasUnsavedChanges || hasImageUnsavedChanges;
    const saveErrorCombined = saveError || imageSaveError;
    const lastSavedCombined = useMemo(() => {
      if (!lastSaved) return lastSavedImage;
      if (!lastSavedImage) return lastSaved;
      return lastSaved > lastSavedImage ? lastSaved : lastSavedImage;
    }, [lastSaved, lastSavedImage]);

    // Field change handler
    const handleFieldChange = useCallback(
      (field: keyof PlanningAspectsData) => {
        return (value: string | string[]) => {
          setFormData((prev) => ({ ...prev, [field]: value }));
        };
      },
      [],
    );

    // Handle site designation selection with special logic for "other"
    const handleSiteDesignationChange = useCallback(
      (selectedDesignations: string[]) => {
        setFormData((prev) => {
          let newDesignations = [...selectedDesignations];

          // If "other" is selected and wasn't before, ensure we include it
          if (
            selectedDesignations.includes("other") &&
            !prev.siteDesignations.includes("other")
          ) {
            newDesignations = [...selectedDesignations];
          }
          // If "other" is deselected, clear the other designation text
          else if (
            !selectedDesignations.includes("other") &&
            prev.siteDesignations.includes("other")
          ) {
            newDesignations = selectedDesignations.filter(
              (designation) => designation !== "other",
            );
            // Clear the other designation text
            setFormData((prev) => ({ ...prev, otherDesignation: "" }));
          }

          return { ...prev, siteDesignations: newDesignations };
        });
      },
      [],
    );

    // Image upload handler
    const handleImageUpload = useCallback(
      (files: FileList | File[] | null, filterData) => {
        if (!files) return;
        const fileArray = Array.from(files);

        const uploadKey = JSON.stringify(filterData);
        setUploadingImages((prev) => ({ ...prev, [uploadKey]: true }));

        requireMutationSuccess(
          dispatch(
            uploadMultipleFiles({
              files: fileArray,
              metadata: {
                siteId,
                entityType: `planning-aspects`,
                entityId: reportId,
                reportId,
              },
              filterData,
            }),
          ),
        )
          .then((mutationOutcome) => {
            // Show success notification for each uploaded file
            fileArray.forEach((file) => {
              const config = notificationHelpers.imageUploadSuccess(
                file.name,
                mutationOutcome,
              );
              notify(config.message, config.type, config.options);
            });
          })
          .catch((error) => {
            // Show error notification
            const config = notificationHelpers.imageUploadError(
              fileArray.length === 1
                ? fileArray[0].name
                : `${fileArray.length} files`,
              errorMessage(error),
            );
            notify(config.message, config.type, config.options);
          })

          .finally(() => {
            setUploadingImages((prev) => ({ ...prev, [uploadKey]: false }));
          });
      },
      [dispatch, siteId, reportId, notify],
    );

    // Update image description
    // Update image description — updates local state; debounced save handles the dispatch
    const updateImageDescription = useCallback(
      (imageId: string, description: string) => {
        setImageDescriptions((prev) => ({ ...prev, [imageId]: description }));
      },
      [setImageDescriptions],
    );

    // Remove image handlers
    const handleRemoveImage = useCallback((imageId: string) => {
      setImageToDelete(imageId);
    }, []);

    const confirmRemoveImage = useCallback(() => {
      if (imageToDelete) {
        requireMutationSuccess(dispatch(deleteFile(imageToDelete)))
          .then((mutationOutcome) => {
            const config = notificationHelpers.imageDeleteSuccess(
              "Image",
              mutationOutcome,
            );
            notify(config.message, config.type, config.options);
          })
          .catch((error) => {
            const config = notificationHelpers.imageDeleteError(
              "Image",
              errorMessage(error),
            );
            notify(config.message, config.type, config.options);
          })
          .finally(() => {
            setImageToDelete(null);
          });
      }
    }, [imageToDelete, dispatch, notify]);

    const cancelRemoveImage = useCallback(() => {
      setImageToDelete(null);
    }, []);

    // Format file size
    const formatFileSize = (bytes: number) => {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return (
        Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
      );
    };

    // Get loading status
    const getLoadingStatus = () => {
      if (saveErrorCombined) return "error";
      if (isSavingCombined) return "saving";
      if (hasUnsavedChangesCombined) return "unsaved";
      if (lastSavedCombined) return "saved";
      return "idle";
    };

    // Check if uploading for a section
    const isUploadingForSection = (filterData: any) => {
      const uploadKey = JSON.stringify(filterData);
      return uploadingImages[uploadKey] || false;
    };

    // Image Card Component
    const ImageCard = useStableMemoComponent(
      function ImageCard({
        image,
        onView,
        onRemove,
        onDescriptionUpdate,
        isDeleting,
        imageDescription,
      }: {
        image: PlanningAspectsImage;
        onView: (image: PlanningAspectsImage) => void;
        onRemove: (id: string) => void;
        onDescriptionUpdate: (id: string, description: string) => void;
        isDeleting: boolean | string;
        imageDescription: string;
      }) {
        return (
          <div key={image.id} className="relative group">
            <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden relative dark:bg-muted">
              <OfflineImage
                fileId={image.id}
                src={image?.fileUrl}
                alt="Planning aspects image"
                className="w-full h-full object-cover"
              />
              {loading && (isDeleting === true || isDeleting === image.id) && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                </div>
              )}
            </div>
            <div className="absolute top-2 right-2 flex gap-1">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
                onClick={() => onView(image)}
                disabled={loading}
                title="View Image"
              >
                <Eye className="w-3 h-3" />
              </Button>
              {!readOnly && (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
                  onClick={() => onRemove(image.id)}
                  disabled={loading}
                  title="Delete Image"
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
            <div className="mt-2 space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate truncate">
                  {image?.originalName}
                </span>
                <Badge variant="outline" className="text-xs">
                  {formatFileSize(image?.fileSize)}
                </Badge>
              </div>
              <OptimizedTextarea
                id={`desc-${image.id}`}
                label=""
                value={imageDescription}
                readOnly={readOnly}
                onChange={(value) => onDescriptionUpdate(image.id, value)}
                placeholder="Describe this planning aspect..."
                rows={2}
                disabled={loading}
              />
              <div className="text-xs text-slate-500 dark:text-muted-foreground">
                Date: {new Date(image.uploadDate).toLocaleDateString()}
              </div>
            </div>
          </div>
        );
      },
      (prevProps, nextProps) => {
        const propsEqual =
          prevProps.image.id === nextProps.image.id &&
          prevProps.imageDescription === nextProps.imageDescription &&
          prevProps.isDeleting === nextProps.isDeleting;
        return propsEqual;
      },
    );

    // Image Section Component
    const ImageSection = useStableMemoComponent(function ImageSection({
      title,
      filterData,
      uploadFilterData,
      emptyMessage,
      buttonText = "Add Images",
    }: {
      title: string;
      filterData: any;
      uploadFilterData: any;
      emptyMessage: string;
      buttonText?: string;
    }) {
      const sectionImages = files?.filter(
        (file) =>
          file?.relatedTo?.entityType === `planning-aspects` &&
          file?.relatedTo?.filterData &&
          JSON.stringify(file?.relatedTo?.filterData) ===
            JSON.stringify(filterData),
      );

      const isUploading = isUploadingForSection(uploadFilterData);

      return (
        <div className="space-y-4 pt-4 border-t">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">{title}</h4>
            {!readOnly && (
              <ImageCapturePicker
                buttonText={buttonText}
                isUploading={isUploading}
                disabled={loading}
                onConfirm={(selectedFiles) =>
                  handleImageUpload(selectedFiles, uploadFilterData)
                }
              />
            )}
          </div>

          {isUploading && (
            <div className="flex items-center justify-center py-6 border border-dashed border-slate-300 rounded-lg bg-slate-50 dark:bg-muted dark:border-border">
              <div className="text-center">
                <Loader2 className="w-6 h-6 text-slate-400 animate-spin mx-auto mb-2 dark:text-muted-foreground" />
                <p className="text-sm text-slate-600 dark:text-muted-foreground">Uploading images...</p>
              </div>
            </div>
          )}

          {!isUploading && sectionImages.length === 0 ? (
            <div className="flex items-center justify-between p-4 border border-dashed border-slate-300 rounded-lg bg-slate-50 dark:bg-muted dark:border-border">
              <div className="flex items-center gap-3">
                <Camera className="w-5 h-5 text-slate-400 dark:text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-foreground">
                    No images added
                  </p>
                  <p className="text-xs text-slate-500 dark:text-muted-foreground">{emptyMessage}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sectionImages.map((image) => (
                <ImageCard
                  key={image.id}
                  imageDescription={
                    imageDescriptions[image.id] ?? image?.fileDescription ?? ""
                  }
                  image={image}
                  onView={setSelectedImage}
                  onRemove={handleRemoveImage}
                  onDescriptionUpdate={updateImageDescription}
                  isDeleting={loading ? imageToDelete : false}
                />
              ))}
            </div>
          )}
        </div>
      );
    });

    return (
      <div className="space-y-4 md:space-y-6 relative">
        {!readOnly && (
          <LocalSectionImportControl
            previousCompletedReports={previousCompletedReports}
            sectionName="planning-aspects"
            siteId={siteId}
            reportId={reportId}
            readOnly={readOnly}
            setFormData={setFormData}
          />
        )}

        {/* Floating save section - hidden when viewing read-only version */}
        {!readOnly && (
          <div className="fixed top-25 right-3 z-40 sm:right-4">
            <div className="flex flex-row items-center gap-2 sm:flex-col sm:items-end sm:gap-3">
              {/* Status indicator - same size as save button */}
              {false && (
                <div
                  className="
          bg-white/95 backdrop-blur-sm
          rounded-full border-2 border-gray-200/80
          shadow-lg
          w-12 h-12 sm:w-16 sm:h-16
          flex items-center justify-center
          group relative
          transition-all duration-300 ease-out
          transform hover:scale-110
          cursor-default
          p-2
        "
                >
                  <div className="flex flex-col items-center justify-center w-full h-full">
                    <span className="text-[10px] font-medium text-gray-600 leading-none mb-2 dark:text-muted-foreground">
                      Saved
                    </span>

                    <div className="flex items-center justify-center h-6">
                      <LoadingIndicator
                        status={getLoadingStatus()}
                        lastSaved={lastSaved}
                        error={saveError}
                      />
                    </div>
                  </div>

                  {/* Tooltip - Fixed with pointer-events-none */}
                  <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                    {getLoadingStatus() === "saving"
                      ? "Saving..."
                      : getLoadingStatus() === "saved"
                        ? `Saved ${lastSaved?.toLocaleTimeString()}`
                        : getLoadingStatus() === "error"
                          ? "Save failed"
                          : "Ready to save"}
                  </div>

                  {/* Error indicator */}
                  {getLoadingStatus() === "error" && (
                    <div className="absolute -top-1 -right-1">
                      <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                      <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                    </div>
                  )}
                </div>
              )}

              {/* Save button - Fixed with pointer-events-none */}
              <Button
                onClick={forceSave}
                disabled={isSaving || readOnly}
                className={`
          relative rounded-full w-12 h-12 sm:w-16 sm:h-16
          transition-all duration-300 ease-out
          flex flex-col items-center justify-center
          backdrop-blur-sm border-2
          shadow-lg hover:shadow-xl
          transform hover:scale-110 active:scale-95
          group
          ${
            hasUnsavedChanges && !readOnly
              ? "bg-gradient-to-br from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white border-red-300 dark:bg-gradient-to-br dark:border-red-300 dark:ring-red-300"
              : "bg-white/95 text-gray-600 border-gray-200/80 hover:bg-white/90 dark:bg-muted dark:text-muted-foreground dark:border-border dark:hover:bg-accent"
          }
          ${isSaving || readOnly ? "opacity-80 cursor-not-allowed" : "cursor-pointer"}
        `}
              >
                {isSaving ? (
                  <div className="flex flex-col items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mb-2"></div>
                    <span className="text-[10px] leading-none">Saving</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center">
                    <Save className="w-5 h-5 sm:mb-1" />
                    <span className="hidden text-[10px] font-medium leading-none sm:block">
                      Save
                    </span>
                  </div>
                )}

                {/* Unsaved changes indicator */}
                {hasUnsavedChanges && !isSaving && (
                  <div className="absolute -top-1 -right-1">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
                    <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                  </div>
                )}

                {/* Tooltip - Fixed with pointer-events-none */}
                <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                  {hasUnsavedChanges ? "Save changes" : "No changes to save"}
                </div>
              </Button>
            </div>
          </div>
        )}

        {/* Planning Aspects Card */}
        <Card>
          <CardHeader>
            <CardTitle>Planning Aspects</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Question 1: Screening conditions */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-slate-700 dark:text-foreground">
                What are the screening conditions of the project?
              </h3>
              <OptimizedTextarea
                id="screeningConditions"
                label="Screening conditions and visual impact considerations"
                value={formData.screeningConditions}
                onChange={handleFieldChange("screeningConditions")}
                placeholder="Describe any screening conditions, visibility requirements, or visual impact considerations..."
                rows={4}
                readOnly={readOnly}
              />
            </div>

            {/* Question 2: Landscape management plan */}
            <div className="space-y-3 pt-4 border-t">
              <h3 className="text-sm font-medium text-slate-700 dark:text-foreground">
                Is there any landscape management plan to be fulfilled?
              </h3>
              <OptimizedTextarea
                id="landscapeManagementPlan"
                label="Landscape management plan requirements"
                value={formData.landscapeManagementPlan}
                onChange={handleFieldChange("landscapeManagementPlan")}
                placeholder="Describe any landscape management requirements, planting schemes, or ecological enhancement plans..."
                rows={4}
                readOnly={readOnly}
              />
            </div>

            {/* Question 3: Site designations */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="text-sm font-medium text-slate-700 dark:text-foreground">
                Does the site have any of the following designations?
              </h3>

              <OptimizedCheckboxGroup
                id="siteDesignations"
                label="Select all planning designations that apply:"
                values={formData.siteDesignations}
                onChange={handleSiteDesignationChange}
                readOnly={readOnly}
                options={siteDesignationOptions}
                direction="column"
                // required
              />

              {formData.siteDesignations.includes("other") && (
                <OptimizedInput
                  id="otherDesignation"
                  label="Please specify the other planning designation"
                  value={formData.otherDesignation}
                  onChange={handleFieldChange("otherDesignation")}
                  placeholder="Please specify the other site designation..."
                  readOnly={readOnly}
                />
              )}

              {formData.siteDesignations.length > 0 && (
                <Alert className="bg-blue-50 border-blue-200">
                  <AlertCircle className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-700">
                    Selected {formData.siteDesignations.length} designation(s).
                    These may have planning implications and require additional
                    considerations.
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <ImageSection
              title="Planning Aspects Images"
              filterData={{ planning_aspects: true }}
              uploadFilterData={{ planning_aspects: true }}
              emptyMessage="Add images related to planning aspects (screening, landscape, designations)"
              buttonText="Add Images"
            />
          </CardContent>
        </Card>

        {/* Planning Aspects Images Card */}
        {/* <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Camera className="w-5 h-5" />
                Planning Aspects Images
              </CardTitle>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const input = document.createElement("input");
                  input.type = "file";
                  input.multiple = true;
                  input.accept = "image/*";
                  input.onchange = (e) => {
                    const target = e.target as HTMLInputElement;
                    handleImageUpload(target.files, {
                      planning_aspects_main: true,
                    });
                  };
                  input.click();
                }}
                disabled={
                  loading ||
                  isUploadingForSection({ planning_aspects_main: true })
                }
              >
                {isUploadingForSection({ planning_aspects_main: true }) ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                {isUploadingForSection({ planning_aspects_main: true })
                  ? "Uploading..."
                  : "Add Images"}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {isUploadingForSection({ planning_aspects_main: true }) && (
              <div className="flex items-center justify-center py-6 border border-dashed border-slate-300 rounded-lg bg-slate-50">
                <div className="text-center">
                  <Loader2 className="w-6 h-6 text-slate-400 animate-spin mx-auto mb-2" />
                  <p className="text-sm text-slate-600">
                    Uploading planning aspects images...
                  </p>
                </div>
              </div>
            )}

            {!isUploadingForSection({ planning_aspects_main: true }) &&
            files?.filter(
              (file) =>
                file?.relatedTo?.entityType === `planning-aspects` &&
                file?.relatedTo?.entityId === reportId &&
                file?.relatedTo?.filterData?.planning_aspects_main,
            ).length === 0 ? (
              <div className="flex items-center justify-between p-4 border border-dashed border-slate-300 rounded-lg bg-slate-50">
                <div className="flex items-center gap-3">
                  <Camera className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm font-medium text-slate-700">
                      No planning aspects images yet
                    </p>
                    <p className="text-xs text-slate-500">
                      Add images to document planning aspects
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {files
                  ?.filter(
                    (file) =>
                      file?.relatedTo?.entityType === `planning-aspects` &&
                      file?.relatedTo?.entityId === reportId &&
                      file?.relatedTo?.filterData?.planning_aspects_main,
                  )
                  .map((image) => (
                  <ImageCard
                    key={image.id}
                    imageDescription={imageDescriptions[image.id] ?? image?.fileDescription ?? ""}
                image={image}
                      onView={setSelectedImage}
                      onRemove={handleRemoveImage}
                      onDescriptionUpdate={updateImageDescription}
                      isDeleting={loading ? imageToDelete : false}
                    />
                  ))}
              </div>
            )}
          </CardContent>
        </Card> */}

        {/* Delete Confirmation Modal */}
        {imageToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-card rounded-lg max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <AlertCircle className="w-6 h-6 text-red-500" />
                <h3 className="text-lg font-semibold">Delete Image</h3>
              </div>
              <p className="text-slate-600 dark:text-muted-foreground mb-6">
                Are you sure you want to delete this image? This action cannot
                be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={cancelRemoveImage}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={confirmRemoveImage}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Image Preview Modal */}
        {selectedImage && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-card rounded-lg max-w-4xl max-h-full overflow-auto">
              <div className="p-4 border-b flex items-center justify-between">
                <h3 className="text-lg font-semibold">
                  {selectedImage.originalName}
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedImage(null)}
                  disabled={loading}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="p-4">
                <OfflineImage
                  fileId={selectedImage.id}
                  src={selectedImage?.fileUrl}
                  alt={selectedImage.originalName}
                  className="max-w-full max-h-96 mx-auto"
                />
                {selectedImage.fileDescription && (
                  <div className="mt-4 p-3 bg-slate-50 dark:bg-muted rounded">
                    <p className="text-sm text-slate-700 dark:text-muted-foreground">
                      {selectedImage.fileDescription}
                    </p>
                  </div>
                )}
                <div className="mt-2 text-sm text-slate-500 dark:text-muted-foreground">
                  Size: {formatFileSize(selectedImage.fileSize)} • Date:{" "}
                  {new Date(selectedImage.uploadDate).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Progress footer */}
        <div className="sticky bottom-2 md:bottom-4 z-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 md:p-4 bg-white dark:bg-card rounded-lg border shadow-lg backdrop-blur-sm bg-white/95 dark:bg-surface-raised/95">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
                Form Status:
              </span>
              <Badge variant="default">Complete</Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
                Progress:
              </span>
              <Badge variant="outline">
                {sessionProgress?.[templateId]?.["planning-aspects"] ||
                  operationalPlanningAspectsProgress(formData)}
                %
              </Badge>
            </div>
          </div>
          <div className="text-xs md:text-sm text-slate-500 dark:text-muted-foreground hidden lg:block">
            Auto-save enabled • Changes saved automatically
          </div>
        </div>
      </div>
    );
  },
);
